// import { ref, computed } from "vue";
import { defineStore } from "pinia";
import {
  getUserProfile as getUserProfileApi,
  getUserPermission,
  checkLogin,
  getExperimentInfoApi,
  getPlacesOfOrigin,
  getUserApiInfo,
} from "@/api";
import { PlatformType } from "@/components/operations";
import { PlatformKey, StatusCode } from "@/config";
import { sentryConfigureScope } from "@wltech/hooks/src/useSentryConfig";
import { getSystemLanguage } from "@/libs/utils";
import { getCountryCodeByName } from "@/libs/country-info";

type PlatformProfile = {
  id: string;
  username: string;
  nickName: string;
  displayName: string;
  firstName: string;
  lastName: string;
  email: string;
};

export type UserInfoType = {
  googleOAuthed: boolean;
  linkedinAuthorized: boolean;
  salesforceAuthorized: boolean;
  googlesheetsAuthorized: boolean;
  notionAuthorized: boolean;
  hubspotAuthorized: boolean;
  zohoAuthorized: boolean;
  authorized: Array<PlatformType>;
  subscribed: boolean;
  linkedinProfile: {
    memorialized: boolean;
    firstName: string;
    lastName: string;
    publicIdentifier: string;
  };
  salesforceProfile: PlatformProfile;
  googlesheetsProfile: PlatformProfile;
  notionProfile: PlatformProfile;
  hubspotProfile: PlatformProfile;
  zohoProfile: PlatformProfile;
  encryptUserId: string;
};

export function getAuthorized(user: UserInfoType): PlatformType[] {
  return Object.keys(PlatformKey).reduce((acc: any, key) => {
    if ((user as any)[`${key}Authorized`] === true) {
      acc.push((PlatformKey as any)[key]);
    }
    return acc;
  }, [] as PlatformType[]);
}

export const useUserStore = defineStore("user", {
  state: () => ({
    user: undefined as UserInfoType | undefined,
    userPermission: undefined as any,
    loginStatus: undefined as any,
    // skipPlatforms: false,
    experimentInfo: undefined as any,
    country: undefined as string | undefined,
    // 是否已领取过 API 免费测试积分（undefined 表示尚未加载）
    hasClaimedFreeApiCredits: undefined as boolean | undefined,
  }),
  getters: {
    userInfo: (state) => Object.assign({}, state.userPermission, state.user),
    getUserPermission: (state) => state.userPermission,
    gettersLoginStatus: (state) => state.loginStatus,
    getExperimentInfoName: (state: any) => (name: string) =>
      state.experimentInfo?.find((item: any) => item.experimentName === name),
    isNewPackage: (state) => state.userPermission?.shouldShowNewPackage,
    // 已订阅
    isSubscribed: (state) => state.userPermission?.subscribed,
    // 特殊订阅：团队版成员 || 已订阅
    isSpecialSubscribed: (state) =>
      state.userPermission?.teamMember || state.userPermission?.subscribed,
    countryCode: (state) => getCountryCodeByName(state.country),
  },
  actions: {
    getUserProfile() {
      return getUserProfileApi({ targetUrl: document.URL })
        .then((res) => {
          if (res.code === 200) {
            this.user = res.data as UserInfoType;
            // this.user.salesforceAuthorized = false;
            // this.user.googlesheetsAuthorized = false;
            // this.user.notionAuthorized = false;
            this.user.authorized = getAuthorized(this.user!);
            return res;
          }
        })
        .catch((e) => {
          console.error(e);
        });
    },
    // setSkipSalesforce(skip: boolean) {
    //   this.skipPlatforms = skip;
    // },
    setUserPermission() {
      return getUserPermission(
        {},
        { systemLanguage: getSystemLanguage() }
      ).then((res) => {
        getPlacesOfOrigin({
          systemLanguage: getSystemLanguage(),
        }).then(res=>{
          if(res.code === StatusCode.Success) {
            this.country = res.data;
          }
        });
        if (res.code === StatusCode.Success) {
          this.userPermission = res.data;
          const { linkedinProfile, firstName, lastName } = res.data || {};
          this.userPermission.authorized = getAuthorized(this.userPermission!);
          sentryConfigureScope({
            userInfo: {
              id: linkedinProfile?.publicIdentifier,
              nick_name: `${firstName} ${lastName}`,
            },
          });
          return res;
        }
      });
    },
    async getCheckLogin() {
      const { code, data } = await checkLogin({ targetUrl: document.URL });
      if (code === StatusCode.Success) {
        this.loginStatus = data;
        return data;
      }
    },
    setHasClaimedFreeApiCredits(claimed: boolean) {
      this.hasClaimedFreeApiCredits = claimed;
    },
    async getApiInfo() {
      try {
        const { code, data } = await getUserApiInfo();
        if (code === StatusCode.Success && data) {
          this.hasClaimedFreeApiCredits = data.hasClaimedFreeApiCredits ?? false;
        }
        return data;
      } catch (e) {
        console.error(e);
      }
    },
    async getExperimentInfo(params: any = {}) {
      const { code, data } = await getExperimentInfoApi(params);
      if (code === StatusCode.Success) {
        this.experimentInfo = data;
        return data;
      }
    },
  },
});
